import { createTheme } from "@mui/material/styles";

/**
 * Talar panel theme — professional-minimal (docs/06 §1, revised 1405-04):
 * hairline borders instead of shadows, warm paper, quiet grays, emerald as
 * the single accent. Density tuned for operators who live in tables.
 *
 * Direction is a parameter, not a constant: Dari and Pashto render RTL,
 * English LTR. The I18nProvider rebuilds the theme when the locale changes and
 * runs RTL styles through stylis-plugin-rtl, so styles here and in `sx` are
 * authored left-to-right and mirrored automatically for RTL.
 */
export function createAppTheme(direction: "rtl" | "ltr" = "rtl") {
  return createTheme({
  direction,
  palette: {
    primary: { main: "#0E6B5C", dark: "#0A4F44", light: "#3D8A7C" },
    // Gold as a text/button colour must clear 4.5:1 — #B8860B is only 3.25:1
    // on white; #8A6300 (already the gold text token in components/ui.tsx) is 5.43:1.
    secondary: { main: "#8A6300", light: "#B8860B", contrastText: "#FFFFFF" },
    background: { default: "#F7F6F2", paper: "#FFFFFF" },
    divider: "#E8E6DF",
    text: { primary: "#1D1C18", secondary: "#6F6B61" },
    error: { main: "#BA1A1A" },
    success: { main: "#1B7A43" },
    warning: { main: "#9A6700" },
  },
  typography: {
    fontFamily: '"Vazirmatn", "Inter", system-ui, sans-serif',
    // 14px base: Perso-Arabic script at 13.5px read small for long sessions.
    fontSize: 14,
    h5: { fontWeight: 800, letterSpacing: "-0.3px" },
    h6: { fontWeight: 700 },
    subtitle2: { fontWeight: 700 },
    button: { fontWeight: 600 },
    overline: { fontWeight: 700, letterSpacing: "1px" },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { textTransform: "none", borderRadius: 8 } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        outlined: { borderColor: "#E8E6DF" },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: "1px solid #E8E6DF", boxShadow: "none" },
      },
    },
    MuiStack: {
      // CSS `gap` instead of per-child margins: correct in both directions and
      // it also spaces rows that wrap.
      defaultProps: { useFlexGap: true },
    },
    MuiTable: {
      defaultProps: { size: "small" },
    },
    MuiTableCell: {
      // Equal-width figures so amounts line up down a column.
      styleOverrides: { root: { fontVariantNumeric: "tabular-nums" } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            backgroundColor: "#FAF9F5",
            fontWeight: 700,
            color: "#6F6B61",
            fontSize: 12,
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:last-child td": { borderBottom: 0 } },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 7 } },
    },
    MuiAlert: {
      styleOverrides: { root: { border: "1px solid", borderColor: "transparent" } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FCFBF8",
          borderRight: "1px solid #E8E6DF",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: "rgba(252, 251, 248, 0.9)", backdropFilter: "blur(8px)" },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "1px 8px",
          paddingTop: 6,
          paddingBottom: 6,
          "&.active": {
            backgroundColor: "rgba(14, 107, 92, 0.09)",
            color: "#0E6B5C",
            "& .MuiListItemIcon-root": { color: "#0E6B5C" },
            "& .MuiTypography-root": { fontWeight: 700 },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36 } },
    },
  },
  });
}
